/** @jsxImportSource preact */
import type { DeductionSource, RefundWaiver, ScoringSettings } from '@plt/refund-quote'
import type { ComponentChildren } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { config } from '../config'
import {
  decideWaiver,
  getRefundScoring,
  publishScoring,
  registerWaiver,
  retireWaiver,
  rollbackScoring,
  type RefundScoringState,
  type StoredWaiver,
} from './refund-scoring-api'

/**
 * Refund confidence settings: the starting score per source and the points each warning costs,
 * airline waivers that lift the score when their conditions are met, and the version history.
 * Changes here alter the confidence badge only, never a refund amount (refund-quote SPEC.md §10.6, §10.7).
 */

const SOURCES: Array<[DeductionSource, string]> = [
  ['VOID_WINDOW', 'Void window'],
  ['OPERATOR_OVERRIDE', 'Agent override'],
  ['SABRE_AUTOMATED_QUOTE', 'Airline quote'],
  ['AIRLINE_WAIVER', 'Airline waiver fee'],
  ['SABRE_TICKET_NO_FEE', 'Refundable, no fee'],
  ['SABRE_TICKET_PENALTY', 'Quoted ticket penalty'],
  ['AIRLINE_POLICY', 'Airline policy (Jetstar)'],
  ['NON_REFUNDABLE_FARE', 'Non-refundable fare'],
  ['STRUCTURED_RULES', 'AI-read fare rules'],
  ['PARSED_FARE_RULES', 'Text-parsed fare rules'],
  ['UNRESOLVED', 'Unresolved'],
]

const STATUS_STYLE: Record<StoredWaiver['status'], string> = {
  pending: 'bg-amber-50 text-amber-800 border-amber-300',
  active: 'bg-green-50 text-green-800 border-green-300',
  rejected: 'bg-gray-50 text-gray-600 border-gray-300',
  retired: 'bg-gray-50 text-gray-600 border-gray-300',
}

const STATUS_LABEL: Record<StoredWaiver['status'], string> = {
  pending: 'Waiting for approval',
  active: 'Live',
  rejected: 'Rejected',
  retired: 'Retired',
}

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
const period = (from: string | null, to: string | null) => (from || to ? `${from ?? 'any'} to ${to ?? 'any'}` : 'Any')

const inputClass =
  'w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50'
const buttonClass =
  'px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
const primaryClass =
  'px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed'

function liveSettings(state: RefundScoringState): ScoringSettings {
  const stored = state.live?.settings
  return {
    baseScores: { ...state.defaults.baseScores, ...stored?.baseScores },
    warningPenalties: { ...state.defaults.warningPenalties, ...stored?.warningPenalties },
    uncertaintySpreadCap: stored?.uncertaintySpreadCap ?? state.defaults.uncertaintySpreadCap,
    searchAgreementPoints: stored?.searchAgreementPoints ?? state.defaults.searchAgreementPoints,
    searchAgreementCarriers: stored?.searchAgreementCarriers ?? state.defaults.searchAgreementCarriers,
  }
}

/** "nz, QF ek" → ['NZ', 'QF', 'EK']; the server rejects anything that is not a two-character code. */
const parseCarriers = (text: string) =>
  text
    .split(/[\s,]+/)
    .map(code => code.trim().toUpperCase())
    .filter(Boolean)
const carriersText = (carriers: string[] | undefined) => (carriers ?? []).join(', ')

export function RefundScoring() {
  const [state, setState] = useState<RefundScoringState | null>(null)
  const [loadError, setLoadError] = useState('')

  const load = async () => {
    try {
      setState(await getRefundScoring())
      setLoadError('')
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load the settings')
    }
  }

  useEffect(() => {
    load()
  }, [])

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-8">
      <div className="flex items-center text-sm">
        <a
          href={config.routePrefix + '/refunds'}
          className="text-indigo-600 hover:text-indigo-800"
        >
          Refunds
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">Scoring settings</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-800">Refund scoring settings</h1>
        <p className="text-sm text-gray-600 mt-1">
          {state?.live
            ? `Live: version ${state.live.version}, published ${when(state.live.publishedAt)} by ${state.live.publishedBy}.`
            : 'Live: the built-in defaults. Nothing has been published yet.'}{' '}
          These settings change the confidence badge only, never a refund amount.
        </p>
        {state && !state.canPublish && !state.canApprove && (
          <p className="text-sm text-gray-500 mt-1">
            You can view these settings. Owners and approvers are set in the stack config.
          </p>
        )}
      </div>

      {loadError && <p className="text-sm text-red-600">{loadError}</p>}
      {!state && !loadError && <p className="text-sm text-gray-500">Loading…</p>}

      {state && (
        <>
          <ScoringSection
            state={state}
            onChanged={load}
          />
          <WaiversSection
            state={state}
            onChanged={load}
          />
          <HistorySection
            state={state}
            onChanged={load}
          />
        </>
      )}
    </div>
  )
}

function ScoringSection({ state, onChanged }: { state: RefundScoringState; onChanged: () => void }) {
  const live = liveSettings(state)
  const [draft, setDraft] = useState<ScoringSettings>(live)
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const [carriers, setCarriers] = useState(carriersText(live.searchAgreementCarriers))

  useEffect(() => {
    const next = liveSettings(state)
    setDraft(next)
    setCarriers(carriersText(next.searchAgreementCarriers))
  }, [state.live?.version])

  const carriersChanged = carriersText(draft.searchAgreementCarriers) !== carriersText(live.searchAgreementCarriers)

  const changes =
    Object.entries(draft.baseScores).filter(([k, v]) => live.baseScores[k as DeductionSource] !== v).length +
    Object.entries(draft.warningPenalties).filter(([k, v]) => live.warningPenalties[k] !== v).length +
    (draft.uncertaintySpreadCap !== live.uncertaintySpreadCap ? 1 : 0) +
    (draft.searchAgreementPoints !== live.searchAgreementPoints ? 1 : 0) +
    (carriersChanged ? 1 : 0)

  const setBase = (source: DeductionSource, value: number) =>
    setDraft({ ...draft, baseScores: { ...draft.baseScores, [source]: value } })
  const setPenalty = (code: string, value: number) =>
    setDraft({ ...draft, warningPenalties: { ...draft.warningPenalties, [code]: value } })

  const publish = async () => {
    if (reason.trim().length < 5) {
      setMessage({ ok: false, text: 'Add a reason before publishing. It goes in the history.' })
      return
    }
    setBusy(true)
    try {
      const version = await publishScoring(draft, reason.trim())
      setMessage({ ok: true, text: `Version ${version.version} is live.` })
      setReason('')
      onChanged()
    } catch (error) {
      setMessage({ ok: false, text: error instanceof Error ? error.message : 'Could not publish' })
    } finally {
      setBusy(false)
    }
  }

  const number = (value: number, onInput: (v: number) => void, disabled: boolean, label: string) => (
    <input
      type="number"
      min={0}
      max={100}
      step={5}
      value={value}
      disabled={disabled}
      aria-label={label}
      onInput={e => onInput(Math.round(Number((e.target as HTMLInputElement).value) || 0))}
      className={inputClass + ' w-20 font-mono text-right'}
    />
  )

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">Scoring</h2>

      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-left px-3 py-2">What it means</th>
              <th className="text-right px-3 py-2">Starting score</th>
            </tr>
          </thead>
          <tbody>
            {SOURCES.map(([source, label]) => {
              const value = draft.baseScores[source] ?? 0
              const changed = value !== live.baseScores[source]
              return (
                <tr
                  key={source}
                  className={'border-t border-gray-100' + (changed ? ' bg-amber-50' : '')}
                >
                  <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{label}</td>
                  <td className="px-3 py-2 text-gray-600">{state.sourceDescriptions[source]}</td>
                  <td className="px-3 py-2 text-right">
                    {number(value, v => setBase(source, v), !state.canPublish || source === 'UNRESOLVED', label)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Warning</th>
              <th className="text-right px-3 py-2">Points off</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(draft.warningPenalties)
              .sort()
              .map(code => {
                const value = draft.warningPenalties[code] ?? 0
                const changed = value !== live.warningPenalties[code]
                return (
                  <tr
                    key={code}
                    className={'border-t border-gray-100' + (changed ? ' bg-amber-50' : '')}
                  >
                    <td className="px-3 py-2">
                      <div className="text-gray-800">{state.warningNotes[code] ?? code}</div>
                      <div className="font-mono text-xs text-gray-400">{code}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {number(value, v => setPenalty(code, v), !state.canPublish, code)}
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      <div
        className={
          'flex items-start justify-between gap-4 border border-gray-200 rounded-md px-3 py-2 text-sm' +
          (draft.uncertaintySpreadCap !== live.uncertaintySpreadCap ? ' bg-amber-50' : '')
        }
      >
        <div>
          <div className="text-gray-800">Widest dollar range a quote can stay high with</div>
          <div className="text-xs text-gray-500">
            When YQ/YR, a fee or a credit is in doubt but no reading changes the case (refund or money owed, something
            or nothing from the supplier, cash or credit), the quote stays high only while the range is at most this
            wide. Wider, it needs a fact-check. In the booking's currency.
          </div>
        </div>
        <input
          type="number"
          min={0}
          max={10000}
          step={10}
          value={draft.uncertaintySpreadCap ?? 0}
          disabled={!state.canPublish}
          aria-label="Widest dollar range a quote can stay high with"
          onInput={e =>
            setDraft({
              ...draft,
              uncertaintySpreadCap: Math.round(Number((e.target as HTMLInputElement).value) || 0),
            })
          }
          className={inputClass + ' w-24 font-mono text-right shrink-0'}
        />
      </div>

      <div
        className={
          'flex items-start justify-between gap-4 border border-gray-200 rounded-md px-3 py-2 text-sm' +
          (draft.searchAgreementPoints !== live.searchAgreementPoints ? ' bg-amber-50' : '')
        }
      >
        <div>
          <div className="text-gray-800">Points for agreeing with the fare search</div>
          <div className="text-xs text-gray-500">
            Added to a quote read from the fare rules when the fare search at booking offered the same penalty. It never
            lifts a quote past 79. 0 turns it off.
          </div>
        </div>
        <input
          type="number"
          min={0}
          max={100}
          step={5}
          value={draft.searchAgreementPoints ?? 0}
          disabled={!state.canPublish}
          aria-label="Points for agreeing with the fare search"
          onInput={e =>
            setDraft({
              ...draft,
              searchAgreementPoints: Math.round(Number((e.target as HTMLInputElement).value) || 0),
            })
          }
          className={inputClass + ' w-24 font-mono text-right shrink-0'}
        />
      </div>

      <div
        className={
          'flex items-start justify-between gap-4 border border-gray-200 rounded-md px-3 py-2 text-sm' +
          (carriersChanged ? ' bg-amber-50' : '')
        }
      >
        <div>
          <div className="text-gray-800">Carriers that earn the agreement points</div>
          <div className="text-xs text-gray-500">
            Airline codes, separated by commas. Elsewhere agreement is shown but adds nothing: on 277 landed refunds the
            fare search matched NZ 8 of 8, other carriers 51-81%. Empty means no carrier.
          </div>
        </div>
        <input
          type="text"
          value={carriers}
          disabled={!state.canPublish}
          aria-label="Carriers that earn the agreement points"
          placeholder="NZ"
          onInput={e => {
            const text = (e.target as HTMLInputElement).value
            setCarriers(text)
            setDraft({ ...draft, searchAgreementCarriers: parseCarriers(text) })
          }}
          className={inputClass + ' w-40 font-mono shrink-0'}
        />
      </div>

      <p className="text-xs text-gray-500">
        High is 80 and above, medium 50 to 79, low 1 to 49, none 0. A booking with mixed sources loses a further 10. A
        quote whose dollar doubts need a fact-check stops at 79.
      </p>

      {state.canPublish && (
        <div className="space-y-2">
          <label
            htmlFor="scoringReason"
            className="block text-sm font-medium text-gray-700"
          >
            Reason, shown in history
          </label>
          <input
            id="scoringReason"
            type="text"
            value={reason}
            onInput={e => setReason((e.target as HTMLInputElement).value)}
            placeholder="Raise airline quote to 95 after 150 exact refunds"
            className={inputClass}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={primaryClass}
              disabled={busy || changes === 0}
              onClick={publish}
            >
              {busy ? 'Publishing…' : `Publish ${changes} change${changes === 1 ? '' : 's'}`}
            </button>
            <button
              type="button"
              className={buttonClass}
              disabled={busy || changes === 0}
              onClick={() => setDraft(live)}
            >
              Discard changes
            </button>
          </div>
        </div>
      )}
      {message && <p className={'text-sm ' + (message.ok ? 'text-green-700' : 'text-red-600')}>{message.text}</p>}
    </section>
  )
}

const EMPTY_WAIVER = {
  code: '',
  carrier: '',
  pccs: '',
  market: '',
  saleFrom: '',
  saleTo: '',
  ticketingFrom: '',
  ticketingTo: '',
  minDays: '',
  codeshareAllowed: false,
  score: '95',
  feePerTicket: '',
  reason: '',
}

function WaiversSection({ state, onChanged }: { state: RefundScoringState; onChanged: () => void }) {
  const [form, setForm] = useState(EMPTY_WAIVER)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const set = (patch: Partial<typeof EMPTY_WAIVER>) => setForm({ ...form, ...patch })

  const register = async () => {
    const missing = [
      !form.code.trim() && 'waiver code',
      !form.carrier.trim() && 'carrier',
      form.reason.trim().length < 5 && 'reason',
    ].filter(Boolean)
    if (missing.length > 0) {
      setMessage({ ok: false, text: `Fill these before registering: ${missing.join(', ')}.` })
      return
    }
    const waiver: RefundWaiver = {
      code: form.code.trim(),
      carrier: form.carrier.trim().toUpperCase(),
      pccs: form.pccs
        .split(',')
        .map(p => p.trim().toUpperCase())
        .filter(Boolean),
      market: form.market || null,
      saleFrom: form.saleFrom || null,
      saleTo: form.saleTo || null,
      ticketingFrom: form.ticketingFrom || null,
      ticketingTo: form.ticketingTo || null,
      minDaysBeforeDeparture: form.minDays === '' ? null : Math.round(Number(form.minDays)),
      codeshareAllowed: form.codeshareAllowed,
      score: Math.round(Number(form.score)),
      airlineFeePerTicket: form.feePerTicket === '' ? null : Number(form.feePerTicket),
    }
    setBusy(true)
    try {
      await registerWaiver(waiver, form.reason.trim())
      setMessage({ ok: true, text: `Waiver ${waiver.code} is registered and waiting for an approver.` })
      setForm(EMPTY_WAIVER)
      onChanged()
    } catch (error) {
      setMessage({ ok: false, text: error instanceof Error ? error.message : 'Could not register the waiver' })
    } finally {
      setBusy(false)
    }
  }

  const act = async (run: () => Promise<unknown>, done: string) => {
    setBusy(true)
    try {
      await run()
      setMessage({ ok: true, text: done })
      onChanged()
    } catch (error) {
      setMessage({ ok: false, text: error instanceof Error ? error.message : 'Could not save' })
    } finally {
      setBusy(false)
    }
  }

  const you = state.you?.toLowerCase()

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">Waivers</h2>
        <p className="text-sm text-gray-600">
          When an agent enters a live waiver's code on a quote and every condition is met, the quote scores at least the
          waiver's score. A waiver with an airline fee also charges that fee on each ticket, in place of the fare rules
          and Sabre; without one the refund amounts do not change. A second person approves each waiver before it goes
          live.
        </p>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Carrier</th>
              <th className="text-left px-3 py-2">PCC</th>
              <th className="text-left px-3 py-2">Sales period</th>
              <th className="text-left px-3 py-2">Ticketing period</th>
              <th className="text-left px-3 py-2">Market</th>
              <th className="text-left px-3 py-2">Codeshare</th>
              <th className="text-left px-3 py-2">Days before departure</th>
              <th className="text-right px-3 py-2">Score</th>
              <th className="text-right px-3 py-2">Fee per ticket</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {state.waivers.length === 0 && (
              <tr>
                <td
                  colSpan={12}
                  className="px-3 py-4 text-center text-gray-500"
                >
                  No waivers yet. Register the first one below.
                </td>
              </tr>
            )}
            {state.waivers.map(w => (
              <tr
                key={w.id}
                className="border-t border-gray-100 align-top"
              >
                <td className="px-3 py-2 font-mono">{w.code}</td>
                <td className="px-3 py-2">{w.carrier}</td>
                <td className="px-3 py-2 font-mono">{w.pccs.length ? w.pccs.join(', ') : 'Any'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{period(w.saleFrom, w.saleTo)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{period(w.ticketingFrom, w.ticketingTo)}</td>
                <td className="px-3 py-2">{w.market ?? 'Any'}</td>
                <td className="px-3 py-2">{w.codeshareAllowed ? 'Allowed' : 'Not allowed'}</td>
                <td className="px-3 py-2">
                  {w.minDaysBeforeDeparture == null ? 'Any' : `${w.minDaysBeforeDeparture}+`}
                </td>
                <td className="px-3 py-2 text-right font-mono">{w.score}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {w.airlineFeePerTicket == null ? 'Unchanged' : w.airlineFeePerTicket.toFixed(2)}
                </td>
                <td className="px-3 py-2">
                  <span className={'inline-block px-2 py-0.5 text-xs rounded-full border ' + STATUS_STYLE[w.status]}>
                    {STATUS_LABEL[w.status]}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">Registered by {w.createdBy}</div>
                  {w.decidedBy && (
                    <div className="text-xs text-gray-500">
                      {w.status === 'rejected' ? 'Rejected' : 'Approved'} by {w.decidedBy}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap space-x-1">
                  {w.status === 'pending' && state.canApprove && w.createdBy.toLowerCase() !== you && (
                    <>
                      <button
                        type="button"
                        className={buttonClass}
                        disabled={busy}
                        onClick={() => act(() => decideWaiver(w.id, 'approve'), `Waiver ${w.code} is live.`)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        className={buttonClass}
                        disabled={busy}
                        onClick={() => act(() => decideWaiver(w.id, 'reject'), `Waiver ${w.code} was rejected.`)}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {w.status === 'active' && state.canPublish && (
                    <button
                      type="button"
                      className={buttonClass}
                      disabled={busy}
                      onClick={() => act(() => retireWaiver(w.id), `Waiver ${w.code} is retired.`)}
                    >
                      Retire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {state.canPublish && (
        <div className="border border-gray-200 rounded-md p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-800">Register a waiver</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field
              id="wCode"
              label="Waiver code"
            >
              <input
                id="wCode"
                className={inputClass + ' font-mono'}
                value={form.code}
                onInput={e => set({ code: (e.target as HTMLInputElement).value })}
                placeholder="PPAZ04926"
              />
            </Field>
            <Field
              id="wCarrier"
              label="Carrier (two letters)"
            >
              <input
                id="wCarrier"
                className={inputClass + ' font-mono'}
                maxLength={2}
                value={form.carrier}
                onInput={e => set({ carrier: (e.target as HTMLInputElement).value })}
                placeholder="FJ"
              />
            </Field>
            <Field
              id="wPccs"
              label="Ticketing PCC (blank for any)"
            >
              <input
                id="wPccs"
                className={inputClass + ' font-mono'}
                value={form.pccs}
                onInput={e => set({ pccs: (e.target as HTMLInputElement).value })}
                placeholder="P0IA, UT8I"
              />
            </Field>
            <Field
              id="wMarket"
              label="Market"
            >
              <select
                id="wMarket"
                className={inputClass}
                value={form.market}
                onChange={e => set({ market: (e.target as HTMLSelectElement).value })}
              >
                <option value="">Any</option>
                <option value="AU">AU</option>
                <option value="NZ">NZ</option>
                <option value="US">US</option>
                <option value="CA">CA</option>
              </select>
            </Field>
            <Field
              id="wSaleFrom"
              label="Sales period from"
            >
              <input
                id="wSaleFrom"
                type="date"
                className={inputClass}
                value={form.saleFrom}
                onInput={e => set({ saleFrom: (e.target as HTMLInputElement).value })}
              />
            </Field>
            <Field
              id="wSaleTo"
              label="Sales period to"
            >
              <input
                id="wSaleTo"
                type="date"
                className={inputClass}
                value={form.saleTo}
                onInput={e => set({ saleTo: (e.target as HTMLInputElement).value })}
              />
            </Field>
            <Field
              id="wTktFrom"
              label="Ticketing period from"
            >
              <input
                id="wTktFrom"
                type="date"
                className={inputClass}
                value={form.ticketingFrom}
                onInput={e => set({ ticketingFrom: (e.target as HTMLInputElement).value })}
              />
            </Field>
            <Field
              id="wTktTo"
              label="Ticketing period to"
            >
              <input
                id="wTktTo"
                type="date"
                className={inputClass}
                value={form.ticketingTo}
                onInput={e => set({ ticketingTo: (e.target as HTMLInputElement).value })}
              />
            </Field>
            <Field
              id="wDays"
              label="Days before departure, at least"
            >
              <input
                id="wDays"
                type="number"
                min={0}
                max={365}
                className={inputClass}
                value={form.minDays}
                onInput={e => set({ minDays: (e.target as HTMLInputElement).value })}
                placeholder="Any"
              />
            </Field>
            <Field
              id="wScore"
              label="Score when it applies"
            >
              <input
                id="wScore"
                type="number"
                min={50}
                max={100}
                className={inputClass}
                value={form.score}
                onInput={e => set({ score: (e.target as HTMLInputElement).value })}
              />
            </Field>
            <Field
              id="wFee"
              label="Airline fee per ticket, in the market's currency"
            >
              <input
                id="wFee"
                type="number"
                min={0}
                step="0.01"
                className={inputClass}
                value={form.feePerTicket}
                onInput={e => set({ feePerTicket: (e.target as HTMLInputElement).value })}
                placeholder="Unchanged"
              />
            </Field>
            <div className="flex items-end pb-1.5">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.codeshareAllowed}
                  onChange={e => set({ codeshareAllowed: (e.target as HTMLInputElement).checked })}
                />
                Codeshare segments allowed
              </label>
            </div>
          </div>
          <Field
            id="wReason"
            label="Reason, shown in the waiver history"
          >
            <input
              id="wReason"
              className={inputClass}
              value={form.reason}
              onInput={e => set({ reason: (e.target as HTMLInputElement).value })}
              placeholder="Fiji Airways waiver confirmed by Michael on 1 Oct"
            />
          </Field>
          <button
            type="button"
            className={primaryClass}
            disabled={busy}
            onClick={register}
          >
            {busy ? 'Saving…' : 'Register and send for approval'}
          </button>
        </div>
      )}
      {message && <p className={'text-sm ' + (message.ok ? 'text-green-700' : 'text-red-600')}>{message.text}</p>}
    </section>
  )
}

function HistorySection({ state, onChanged }: { state: RefundScoringState; onChanged: () => void }) {
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const rollback = async (version: number) => {
    if (reason.trim().length < 5) {
      setMessage({ ok: false, text: 'Add a reason for the roll back first. It goes in the history.' })
      return
    }
    setBusy(true)
    try {
      const restored = await rollbackScoring(version, reason.trim())
      setMessage({ ok: true, text: `Version ${restored.version} is live, with the numbers from version ${version}.` })
      setReason('')
      onChanged()
    } catch (error) {
      setMessage({ ok: false, text: error instanceof Error ? error.message : 'Could not roll back' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-800">History</h2>
      {state.history.length === 0 ? (
        <p className="text-sm text-gray-500">No versions published yet. Quotes use the built-in defaults.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Version</th>
                <th className="text-left px-3 py-2">Published</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {state.history.map((v, i) => (
                <tr
                  key={v.version}
                  className="border-t border-gray-100"
                >
                  <td className="px-3 py-2 font-mono">v{v.version}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{when(v.publishedAt)}</td>
                  <td className="px-3 py-2">{v.publishedBy}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {v.reason}
                    {v.restoredFrom != null && (
                      <span className="text-gray-500"> (restored from v{v.restoredFrom})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {i === 0 ? (
                      <span className="inline-block px-2 py-0.5 text-xs rounded-full border bg-green-50 text-green-800 border-green-300">
                        Live
                      </span>
                    ) : (
                      state.canPublish && (
                        <button
                          type="button"
                          className={buttonClass}
                          disabled={busy}
                          onClick={() => rollback(v.version)}
                        >
                          Roll back to this
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {state.canPublish && state.history.length > 1 && (
        <div className="space-y-1">
          <label
            htmlFor="rollbackReason"
            className="block text-sm font-medium text-gray-700"
          >
            Reason for a roll back
          </label>
          <input
            id="rollbackReason"
            className={inputClass}
            value={reason}
            onInput={e => setReason((e.target as HTMLInputElement).value)}
            placeholder="High share jumped without matching refunds"
          />
        </div>
      )}
      {message && <p className={'text-sm ' + (message.ok ? 'text-green-700' : 'text-red-600')}>{message.text}</p>}
    </section>
  )
}

function Field({ id, label, children }: { id: string; label: string; children: ComponentChildren }) {
  return (
    <div className="space-y-1">
      <label
        htmlFor={id}
        className="block text-xs font-medium text-gray-600"
      >
        {label}
      </label>
      {children}
    </div>
  )
}
